import { authFetch } from '../../../utils/authFetch';
import { sameWorkspaceId } from '../../../utils/workspaceId';
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card as PxCard } from '@roi-pros/px-react';
import toast from 'react-hot-toast';
import { PlanPicker } from './PlanPicker';
import type { PendingPlanChange } from '@bb-dev123/central-billing';

interface PlanInfo {
  code: string;
  name: string;
  baseCredits: number;
  includedSeats: number;
  perSeatCents: number;
  maxSeats: number | null;
}

interface OutcomeInfo {
  code: string;
  displayName: string;
  includedCredits: number;
}

interface SeatsInfo {
  current: number;
  included: number;
  additional: number;
  perSeatCents: number;
  subtotalCents: number;
}

interface CreditsInfo {
  fromPlan: number;
  fromOutcomes: number;
  monthlyIncluded: number;
  purchasedRemaining: number;
  available: number;
}

interface PricingData {
  plan: PlanInfo;
  outcomes: OutcomeInfo[];
  seats: SeatsInfo;
  credits: CreditsInfo;
  subtotalCents: number;
  totalCents: number;
}

interface SubscriptionData {
  /** The TOKEN's workspace (subscription/me is token-scoped). */
  workspaceId?: string;
  planCode: string;
  planName: string;
  status: 'active' | 'trialing' | 'past_due' | 'canceled';
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  isActive: boolean;
  trialEndsAt?: string | null;
  trialExpired?: boolean;
  /** 2026-10-12 contract: scheduled change, live-subscription flag, caller's billing permission. */
  pendingChange?: PendingPlanChange | null;
  hasLiveSubscription?: boolean;
  permissions?: { canManageBilling: boolean; canViewInvoices: boolean };
}

interface BillingTabProps {
  workspaceId: string;
  authToken: string;
}

const statusLabels: Record<string, string> = {
  active: 'Active',
  trialing: 'Trial',
  past_due: 'Past Due',
  canceled: 'Canceled',
};

const statusColors: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  trialing: 'bg-amber-100 text-amber-700',
  past_due: 'bg-red-100 text-red-700',
  canceled: 'bg-gray-100 text-gray-500',
};

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Billing settings tab
 *
 * Features:
 * - Current plan info
 * - Billing status
 * - Trial status
 * - Monthly total breakdown
 * - Outcome pricing
 * - Seat pricing
 * - Stripe portal link
 */
export function BillingTab({ workspaceId, authToken }: BillingTabProps) {
  const [pricing, setPricing] = useState<PricingData | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);
  const [hasStripeCustomer, setHasStripeCustomer] = useState(false);
  const [hasStripeSubscription, setHasStripeSubscription] = useState(false);
  // Caller's role IN THIS workspace and the workspace type (plan filtering).
  // null = unknown (lookup failed) — never treated as owner/admin.
  const [role, setRole] = useState<string | null>(null);
  const [workspaceType, setWorkspaceType] = useState<string | null>(null);
  const [notice, setNotice] = useState('');

  // Contract §1.2: `?view=plans` opens the plan picker; `return_to` (or the
  // settings page's `returnUrl`) is where checkout should finally return;
  // `billing=success|cancel` is set by /billing/return when it lands here.
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('view');
  const returnTo = searchParams.get('return_to') || searchParams.get('returnUrl');
  const billingResult = searchParams.get('billing');

  useEffect(() => {
    loadData();
  }, [workspaceId]);

  useEffect(() => {
    if (billingResult === 'success') {
      setNotice('Billing updated. It can take a moment for changes to appear here.');
    } else if (billingResult === 'cancel') {
      setNotice('Checkout canceled. No payment was taken and nothing was changed.');
    }
  }, [billingResult]);

  async function loadData() {
    try {
      const [pricingRes, subscriptionRes, providerRes, workspaceRes] = await Promise.all([
        authFetch(`/api/workspaces/${workspaceId}/pricing`, authToken),
        authFetch('/api/subscription/me', authToken),
        authFetch('/api/billing/provider', authToken),
        authFetch(`/api/workspaces/${workspaceId}`, authToken).catch(() => null),
      ]);

      if (pricingRes.ok) {
        const data = await pricingRes.json();
        setPricing(data.pricing || null);
      }

      if (subscriptionRes.ok) {
        const data = await subscriptionRes.json();
        setSubscription({
          workspaceId: data.workspaceId,
          planCode: data.planCode,
          planName: data.planName,
          status: data.status,
          currentPeriodEnd: data.currentPeriodEnd,
          cancelAtPeriodEnd: data.cancelAtPeriodEnd,
          isActive: data.isActive,
          trialEndsAt: data.trialEndsAt ?? null,
          trialExpired: data.trialExpired === true,
          pendingChange: data.pendingChange ?? null,
          ...(typeof data.hasLiveSubscription === 'boolean' ? { hasLiveSubscription: data.hasLiveSubscription } : {}),
          ...(data.permissions && typeof data.permissions.canManageBilling === 'boolean' ? { permissions: data.permissions } : {}),
        });
      }

      if (providerRes.ok) {
        const data = await providerRes.json();
        setHasStripeCustomer(data.customerIdPresent || false);
        setHasStripeSubscription(data.subscriptionIdPresent || false);
      }

      if (workspaceRes && workspaceRes.ok) {
        const data = await workspaceRes.json().catch(() => null);
        setRole(typeof data?.membership?.role === 'string' ? data.membership.role : null);
        setWorkspaceType(typeof data?.workspace?.type === 'string' ? data.workspace.type : null);
      }
    } catch {
      toast.error('Failed to load billing information');
    } finally {
      setIsLoading(false);
    }
  }

  function setView(next: 'plans' | null) {
    const params = new URLSearchParams(searchParams);
    if (next) params.set('view', next);
    else params.delete('view');
    params.delete('billing');
    setSearchParams(params, { replace: true });
  }

  async function handleOpenPortal() {
    setIsOpeningPortal(true);
    try {
      const response = await authFetch('/api/subscriptions/portal', authToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // workspaceId: the portal acts on the TOKEN workspace; the server
        // refuses (409) if that is not the workspace on screen.
        body: JSON.stringify({ return_to: window.location.href, workspaceId }),
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.url;
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to open billing portal');
      }
    } catch {
      toast.error('Failed to open billing portal');
    } finally {
      setIsOpeningPortal(false);
    }
  }

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-900/5 p-8 text-center">
        <div className="animate-spin w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full mx-auto"></div>
      </div>
    );
  }

  // Central's own answer (DB role, the billing routes' predicate) when the
  // API sends it; else the workspace role read above (owner/admin).
  const canManage = subscription?.permissions ? subscription.permissions.canManageBilling : role === 'owner' || role === 'admin';
  // Unknown role (lookup failed) is not a manager, but is not proven a member
  // either: plan actions stay hidden, and Manage keeps its previous rule (the
  // server enforces owner/admin on the portal regardless).
  const knownNonManager = (subscription?.permissions !== undefined || role !== null) && !canManage;
  // /api/subscription/me and the billing APIs act on the TOKEN's workspace.
  // If that is not the workspace on screen, showing its plan here — or
  // offering checkout — would bill the wrong workspace.
  // CEN-CASE: UUID-canonical — an upper-case URL id is the same workspace.
  const workspaceMismatch =
    !!subscription?.workspaceId && !sameWorkspaceId(subscription.workspaceId, workspaceId);
  const trialExpired = subscription?.trialExpired === true;
  // `/api/billing/provider` is billing-manager-only now (raw provider ids); the
  // subscription read says whether a live subscription exists for everyone.
  const hasLiveSubscription =
    typeof subscription?.hasLiveSubscription === 'boolean'
      ? subscription.hasLiveSubscription
      : hasStripeSubscription && subscription?.status !== 'canceled';
  const trialEnd = subscription?.trialEndsAt || subscription?.currentPeriodEnd || null;

  if (workspaceMismatch) {
    const continueTo = encodeURIComponent(window.location.pathname + window.location.search);
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6" role="alert">
        <p className="font-medium text-amber-900">You are signed in to a different workspace</p>
        <p className="text-sm text-amber-800 mt-1">
          Billing for this workspace can only be viewed or changed while you are signed in to it.
        </p>
        <a
          href={`/auth/workspace?continue=${continueTo}`}
          className="inline-block mt-4 px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700"
        >
          Switch workspace
        </a>
      </div>
    );
  }

  if (view === 'plans') {
    return (
      <PlanPicker
        workspaceId={workspaceId}
        authToken={authToken}
        workspaceType={workspaceType}
        currentPlanCode={subscription?.planCode ?? pricing?.plan.code ?? null}
        canManage={canManage}
        hasLiveSubscription={hasLiveSubscription}
        seatCount={pricing?.seats.current ?? null}
        returnTo={returnTo}
        pendingChange={subscription?.pendingChange ?? null}
        onBack={() => setView(null)}
        onChanged={(message) => {
          setNotice(message);
          setView(null);
          loadData();
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      {notice && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4" role="status">
          <p className="text-sm text-emerald-800">{notice}</p>
        </div>
      )}

      {/* Payment problems / ended subscriptions — always say what to do next */}
      {subscription?.status === 'past_due' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4" role="alert">
          <p className="font-medium text-red-900">Your last payment failed</p>
          <p className="text-sm text-red-800 mt-1">
            {knownNonManager
              ? 'Ask your workspace owner to update the payment method.'
              : 'Update your payment method to keep your plan.'}
          </p>
          {!knownNonManager && hasStripeCustomer && (
            <button
              type="button"
              onClick={handleOpenPortal}
              disabled={isOpeningPortal}
              className="mt-3 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              Update payment method
            </button>
          )}
        </div>
      )}
      {(subscription?.status === 'canceled' || trialExpired) && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" role="alert">
          <p className="font-medium text-amber-900">
            {trialExpired ? 'Your trial has ended' : 'Your subscription is canceled'}
          </p>
          <p className="text-sm text-amber-800 mt-1">
            {canManage
              ? 'Choose a plan to keep using paid features.'
              : 'Ask your workspace owner to choose a plan.'}
          </p>
          {canManage && (
            <button
              type="button"
              onClick={() => setView('plans')}
              className="mt-3 px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700"
            >
              Choose a plan
            </button>
          )}
        </div>
      )}

      {/* Current Plan & Status */}
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-900/5 p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-medium text-gray-900">Current Plan</h2>
            <p className="text-2xl font-semibold text-gray-900 mt-2">
              {pricing?.plan.name || subscription?.planName || 'Free'}
            </p>
            {subscription && (
              <div className="mt-3 flex items-center gap-3">
                <span
                  className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                    statusColors[subscription.status] || statusColors.active
                  }`}
                >
                  {trialExpired ? 'Trial ended' : statusLabels[subscription.status] || subscription.status}
                </span>
                {subscription.status === 'trialing' && trialEnd && (
                  <span className="text-sm text-gray-500">
                    {trialExpired ? 'Trial ended' : 'Trial ends'} {formatDate(trialEnd)}
                  </span>
                )}
                {subscription.cancelAtPeriodEnd && !subscription.pendingChange && (
                  <span className="text-sm text-red-600">
                    Cancels {formatDate(subscription.currentPeriodEnd)}
                  </span>
                )}
                {subscription.pendingChange && (
                  <span className="text-sm text-blue-700" data-testid="billing-pending-change">
                    Changes to {subscription.pendingChange.planName ?? subscription.pendingChange.planCode}{' '}
                    {formatDate(subscription.pendingChange.effectiveAt ?? subscription.currentPeriodEnd)}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-2">
            {canManage && (
              <button
                type="button"
                onClick={() => setView('plans')}
                className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700"
              >
                {hasLiveSubscription ? 'Change plan' : 'Upgrade'}
              </button>
            )}
            {hasStripeCustomer && !knownNonManager && (
              <button
                onClick={handleOpenPortal}
                disabled={isOpeningPortal}
                className="px-4 py-2 text-sm font-medium text-emerald-600 border border-emerald-600 rounded-lg hover:bg-emerald-50 disabled:opacity-50"
              >
                {isOpeningPortal ? 'Opening...' : 'Manage Billing'}
              </button>
            )}
          </div>
        </div>
        {knownNonManager && (
          <p className="text-sm text-gray-500 mt-4">
            Only workspace owners and admins can change the plan. Ask your workspace owner.
          </p>
        )}
        {subscription?.currentPeriodEnd && subscription.status !== 'trialing' && (
          <p className="text-sm text-gray-500 mt-4">
            Next billing date: {formatDate(subscription.currentPeriodEnd)}
          </p>
        )}
      </div>

      {/* Monthly Total */}
      {pricing && (
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-900/5 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Monthly Summary</h2>
          <div className="space-y-4">
            {/* Base Plan */}
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="text-sm font-medium text-gray-900">{pricing.plan.name} Plan</p>
                <p className="text-sm text-gray-500">
                  {pricing.plan.baseCredits} credits included
                </p>
              </div>
              <p className="text-sm font-medium text-gray-900">
                Included
              </p>
            </div>

            {/* Outcomes */}
            {pricing.outcomes.length > 0 && (
              <div className="border-t border-gray-100 pt-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
                  Active Outcomes
                </p>
                {pricing.outcomes.map((outcome) => (
                  <div key={outcome.code} className="flex items-center justify-between py-2">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{outcome.displayName}</p>
                      {outcome.includedCredits > 0 && (
                        <p className="text-sm text-gray-500">
                          +{outcome.includedCredits} credits/mo
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Seats */}
            {pricing.seats.additional > 0 && (
              <div className="border-t border-gray-100 pt-4">
                <div className="flex items-center justify-between py-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Additional Seats</p>
                    <p className="text-sm text-gray-500">
                      {pricing.seats.additional} seats × {formatCents(pricing.seats.perSeatCents)}/seat
                    </p>
                  </div>
                  <p className="text-sm font-medium text-gray-900">
                    {formatCents(pricing.seats.subtotalCents)}
                  </p>
                </div>
              </div>
            )}

            {/* Total */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <div className="flex items-center justify-between">
                <p className="text-base font-semibold text-gray-900">Monthly Total</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatCents(pricing.totalCents)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Credits Summary */}
      {/* B8 Platform adoption: this read-only "Credits Allocation" information panel now
          uses the Platform Experience Card (@roi-pros/px-react) as its surface/container —
          the Platform owns the panel chrome (surface/border/radius/padding via tokens).
          The application retains its heading and all derived values (rendered as children).
          The app keeps its own <h2> rather than Card's `title` prop: Card renders a fixed
          <h3>, and forcing this one settings section to h3 would break the page's heading
          hierarchy (see Platform gap in the B8 report). Presentation only; no call sites. */}
      {pricing && (
        <PxCard api={{}}>
          <h2 className="text-lg font-medium text-gray-900 mb-4">Credits Allocation</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">From Plan</p>
              <p className="text-xl font-semibold text-gray-900">{pricing.credits.fromPlan}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500">From Outcomes</p>
              <p className="text-xl font-semibold text-gray-900">{pricing.credits.fromOutcomes}</p>
            </div>
            <div className="bg-emerald-50 rounded-lg p-4 col-span-2">
              <p className="text-sm text-emerald-600">Monthly Included</p>
              <p className="text-2xl font-bold text-emerald-700">{pricing.credits.monthlyIncluded}</p>
            </div>
          </div>
          {pricing.credits.purchasedRemaining > 0 && (
            <div className="mt-4 p-3 bg-green-50 rounded-lg">
              <p className="text-sm text-green-600">
                You have <span className="font-semibold">{pricing.credits.purchasedRemaining}</span> purchased credits remaining
              </p>
            </div>
          )}
        </PxCard>
      )}

      {/* Seat Details */}
      {pricing && (
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-900/5 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Team Seats</h2>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-3xl font-bold text-gray-900">
                {pricing.seats.current}
                <span className="text-lg font-normal text-gray-500">
                  {' '}/ {pricing.seats.included} included
                </span>
              </p>
              {pricing.seats.additional > 0 && (
                <p className="text-sm text-gray-500 mt-1">
                  {pricing.seats.additional} additional seats at {formatCents(pricing.seats.perSeatCents)}/seat
                </p>
              )}
            </div>
            {pricing.plan.maxSeats && (
              <div className="text-right">
                <p className="text-sm text-gray-500">Max seats</p>
                <p className="text-lg font-medium text-gray-900">{pricing.plan.maxSeats}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Billing Portal */}
      {!hasStripeCustomer && (
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-6 text-center">
          <svg
            className="w-12 h-12 text-gray-400 mx-auto mb-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
            />
          </svg>
          <p className="text-gray-600 font-medium">No payment method on file</p>
          <p className="text-sm text-gray-500 mt-1">
            Add a payment method to upgrade your plan or purchase credits
          </p>
        </div>
      )}
    </div>
  );
}
